// Shared client/server protocol. Imported by both the Worker and web/.

import type { Lang } from "./languages";

/**
 * Path the app is mounted on: it is served as part of the WorkBrawl hub at
 * https://workbrawl.com/stickbrawl/. Static files live in public/stickbrawl/ to match.
 */
export const BASE_PATH = "/stickbrawl";

/** People per room. PLAYER_COLORS must have at least this many colors. */
export const MAX_PLAYERS = 12;

/** Round lengths the room creator can pick, in seconds. */
export const DURATIONS = [120, 180, 300] as const;
export type Duration = (typeof DURATIONS)[number];
export const DEFAULT_DURATION: Duration = 180;

export const COUNTDOWN_MS = 3000;
/** How long each person has to pick a letter before the turn moves on. */
export const TURN_MS = 10_000;
/** How long a finished puzzle (solved or lost) stays on screen before the next one. */
export const REVEAL_MS = 2500;

/** Points for each place a guessed letter appears in the puzzle. */
export const LETTER_POINTS = 1;
/** Extra points for the letter that completes the puzzle. */
export const SOLVE_BONUS = 2;
/** Wrong letters allowed before a puzzle is lost: the parts of the stick figure. */
export const MISSES_WORD = 6;
/** Phrases are longer, so they get one more (the figure's hat). */
export const MISSES_PHRASE = 7;

/** What the puzzles are about. "mixed" draws from every category. */
export const CATEGORIES = ["sayings", "everyday", "food", "places", "office", "nature"] as const;
export type Category = (typeof CATEGORIES)[number];
export const MIXED = "mixed";
export type CategoryChoice = Category | typeof MIXED;

/**
 * lobby: waiting for someone to start. countdown: 3-2-1 before a round.
 * running: the team takes turns guessing letters while the clock counts down.
 * finished: the team's result is on screen.
 */
export type RoomStatus = "lobby" | "countdown" | "running" | "finished";

// Mid-tone colors that keep contrast on both light and dark backgrounds. Indexed by slot.
export const PLAYER_COLORS = [
  "#e5484d", // red
  "#0b84e5", // blue
  "#2f9e44", // green
  "#f08c00", // orange
  "#8e4ec6", // purple
  "#0f9d9a", // teal
  "#d6409f", // pink
  "#a0703c", // brown
  "#c79a00", // gold
  "#5b5bd6", // indigo
  "#7aa116", // lime
  "#6b7785", // slate
];

export interface PlayerInfo {
  id: string;
  name: string;
  /** 0..MAX_PLAYERS-1: picks the color and fixes the turn order (who joined first). */
  slot: number;
  /** Points this person added to the team in the current (or last) round. */
  score: number;
  online: boolean;
}

/** How the last finished round went for the team. */
export interface RoundSummary {
  /** Team total: everyone's points added up. */
  score: number;
  solved: number;
  lost: number;
  /** Who added what, best first. */
  players: { id: string; name: string; slot: number; score: number }[];
  /** The puzzle the clock interrupted, if any, so nobody is left wondering. */
  unfinished: string | null;
}

export type ClientMsg = { type: "guess"; letter: string } | { type: "start" } | { type: "ping" };

/** JSON body of POST /api/rooms. */
export interface CreateRoomRequest {
  duration?: Duration; // omitted = DEFAULT_DURATION
  lang?: Lang; // language of the puzzles; omitted = English
  category?: CategoryChoice; // omitted = mixed
  turnstileToken: string;
}

/** JSON body of POST /api/rooms/:code/verify (join by code from the home screen). */
export interface VerifyRoomRequest {
  turnstileToken: string;
}

/** Response of GET /api/config. */
export interface ClientConfig {
  turnstileSiteKey: string;
}

/** Error codes returned as `{ error }` by the HTTP API. */
export type ApiErrorCode = "INVALID_REQUEST" | "TURNSTILE_FAILED" | "ROOM_NOT_FOUND";

/** guess: waiting for a letter. solved / lost: showing the answer before the next puzzle. */
export type PuzzlePhase = "guess" | "solved" | "lost";

/** The puzzle on screen, as every client sees it. */
export interface PuzzleView {
  /** 1 for the first puzzle of the round, 2 for the second… */
  n: number;
  category: Category;
  /**
   * One entry per character: the character itself when it is known (a guessed letter,
   * a space or punctuation) and "_" for a letter still hidden. The answer is never
   * sent while the puzzle is open.
   */
  pattern: string[];
  /** Letters tried so far that are in the puzzle / are not. */
  hits: string;
  wrong: string;
  maxMisses: number;
  phase: PuzzlePhase;
  /** Whose turn it is (player id). Null outside "guess". */
  turn: string | null;
  /** The letter just tried, for the animation. */
  last: { by: string; letter: string; found: number } | null;
  /** Team totals for the round so far. */
  score: number;
  solved: number;
  lost: number;
}

export type ServerMsg =
  | {
      type: "state";
      you: string;
      status: RoomStatus;
      /** Round length in seconds. */
      duration: Duration;
      /** Language of the puzzles (fixed per room). */
      lang: Lang;
      category: CategoryChoice;
      /** Milliseconds left in the countdown (status "countdown") or the round ("running"). */
      remainingMs: number;
      players: PlayerInfo[];
      /** Last finished round. Null before the first one ends. */
      summary: RoundSummary | null;
    }
  | ({
      /** Sent whenever something about the puzzle changes. */
      type: "puzzle";
      /** Milliseconds left in the round and in the current turn. */
      t: number;
      turnMs: number;
      /** [slot, points] for every player. */
      p: [number, number][];
    } & PuzzleView)
  | { type: "player_joined"; player: PlayerInfo }
  | { type: "player_updated"; player: PlayerInfo }
  | { type: "player_left"; id: string }
  | { type: "error"; code: ErrorCode; message: string }
  | { type: "pong" };

/**
 * Exact keep-alive frames. The room registers them as a WebSocket auto-response, so
 * Cloudflare answers pings at the edge WITHOUT waking the Durable Object: an idle room
 * with open tabs stays hibernated (no billable duration). The client must send PING_FRAME
 * byte for byte, or the ping falls through to the room and wakes it.
 */
export const PING_FRAME = '{"type":"ping"}';
export const PONG_FRAME = '{"type":"pong"}';

export type ErrorCode = "ROOM_FULL" | "ROOM_NOT_FOUND" | "BAD_MESSAGE";

export const ROOM_CODE_ALPHABET = "ABCDEFGHJKMNPQRSTUVWXYZ23456789";
export const ROOM_CODE_LENGTH = 5;
export const NAME_MAX_LENGTH = 20;

const ROOM_CODE_RE = new RegExp(`^[${ROOM_CODE_ALPHABET}]{${ROOM_CODE_LENGTH}}$`);
const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

export function isValidRoomCode(code: string): boolean {
  return ROOM_CODE_RE.test(code);
}

export function isValidPlayerId(id: string): boolean {
  return UUID_RE.test(id);
}

export function isDuration(value: unknown): value is Duration {
  return (DURATIONS as readonly unknown[]).includes(value);
}

export function isCategoryChoice(value: unknown): value is CategoryChoice {
  return value === MIXED || (CATEGORIES as readonly unknown[]).includes(value);
}

/** Letters people can guess: A–Z, plus Ñ in Spanish. */
export function alphabet(lang: Lang): string[] {
  const letters = "ABCDEFGHIJKLMNOPQRSTUVWXYZ".split("");
  if (lang === "es") letters.splice(letters.indexOf("N") + 1, 0, "Ñ");
  return letters;
}

/**
 * The letter a character counts as when guessing: uppercase, accents and diaereses removed
 * (Á → A, Ü → U) but Ñ kept as its own letter. Returns "" for anything that is not a letter
 * (spaces, punctuation, digits), which is always shown.
 */
export function baseLetter(char: string): string {
  const upper = char.toUpperCase();
  if (upper === "Ñ") return "Ñ";
  const plain = upper.normalize("NFD").replace(/[̀-ͯ]/g, "");
  return /^[A-Z]$/.test(plain) ? plain : "";
}

/**
 * Trims and collapses whitespace. Returns null when the name is empty, longer than
 * NAME_MAX_LENGTH characters, or contains HTML-ish or control characters.
 */
export function sanitizeName(raw: string): string | null {
  const name = raw.trim().replace(/\s+/g, " ");
  const length = [...name].length;
  if (length < 1 || length > NAME_MAX_LENGTH) return null;
  if (/[<>&"'`\p{Cc}]/u.test(name)) return null;
  return name;
}

/** Best score first; ties keep the lower slot (who joined earlier) ahead. */
export function rank<T extends { score: number; slot: number }>(players: readonly T[]): T[] {
  return [...players].sort((a, b) => b.score - a.score || a.slot - b.slot);
}
